import { CheckIcon } from '@heroicons/react/20/solid';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Arrow } from '../../../icons/SvgIcons';
import { useCloseOnOutsideInteraction } from '../../../hooks/useCloseOnOutsideInteraction';
import { FilterDropdownProps } from './FilterDropdown.types';

export function FilterDropdown({
  placeholder,
  options,
  selectedValues,
  onSelectionChange,
  className = '',
  showSelectedCount = true
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0, width: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useCloseOnOutsideInteraction(isOpen, [wrapperRef, menuRef], () =>
    setIsOpen(false)
  );

  const handleToggleOpen = () => {
    if (!isOpen && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setMenuPosition({ left: rect.left, top: rect.bottom + 4, width: rect.width });
    }
    setIsOpen(!isOpen);
  };

  const handleOptionToggle = (value: string) => {
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];

    onSelectionChange(newSelectedValues);
  };

  const selectedCount = selectedValues.length;

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      <button
        type="button"
        onClick={handleToggleOpen}
        className={`
          flex items-center justify-between w-full
          px-[13px] py-[9px] gap-1
          text-sm font-medium text-gray-800
          bg-white rounded-md
          hover:bg-gray-50 focus:outline-none
          ${
            isOpen
              ? 'ring-2 ring-purple-700 shadow-sm'
              : 'ring-1 ring-gray-250v2'
          }
        `}
      >
        <div className="flex w-full justify-between">
          <span className="text-sm font-medium leading-5">{placeholder}</span>
          {showSelectedCount && selectedCount > 0 && (
            <div
              className="
              inline-flex items-center justify-center
              min-w-[26px] h-5 px-[10px] py-[2px]
              bg-purple-100 rounded-[10px]
            "
            >
              <span className="text-xs font-medium text-gray-700">
                {selectedCount}
              </span>
            </div>
          )}
        </div>

        <div
          className={`transition-transform duration-300  ${
            isOpen ? 'rotate-180' : 'rotate-0'
          }`}
        >
          {Arrow('down')}
        </div>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed min-w-max bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-auto"
            style={{
              left: `${menuPosition.left}px`,
              top: `${menuPosition.top}px`,
              width: `${menuPosition.width}px`,
              zIndex: 999
            }}
          >
            {options.length === 0 && (
              <div className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                No options available
              </div>
            )}
            {options.map((option) => {
              const isSelected = selectedValues.includes(option.value);

              return (
                <div
                  key={option.value}
                  onClick={() => handleOptionToggle(option.value)}
                  className="
                  flex items-center gap-2
                  px-4 py-2 cursor-pointer
                  hover:bg-gray-50
                "
                >
                  <div
                    className={`
                  w-4 h-4 rounded border
                  flex items-center justify-center
                  ${
                    isSelected
                      ? 'bg-purple-500 border-purple-500'
                      : 'bg-white border-gray-200'
                  }
                `}
                  >
                    {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                  </div>

                  <span className="text-sm font-normal leading-5 text-gray-800 whitespace-nowrap">
                    {option.label}
                  </span>
                </div>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}
